import {BaseApi} from './BaseApi'
import {sanitizePayload} from '../utils/apiUtils'

// Position Enums
export enum PositionHireType {
  W2 = 'W2',
  C1099 = '1099',
  TEMP = 'Temporary',
  TEMP_TO_HIRE = 'Temp to Hire',
  DIRECT_HIRE = 'Direct Hire',
  CONTRACT = 'Contract',
}

export enum PositionShift {
  DAY = 'Day Shift',
  NIGHT = 'Night Shift',
  EVENING = 'Evening Shift',
  ROTATING = 'Rotating Shift',
  FLEXIBLE = 'Flexible',
}

// Position Interfaces
export interface Position {
  id: string
  positionName: string
  shift: PositionShift
  payRateMin: number
  payRateMax: number
  companyId: string
  regionId: string
  siteId: string
  departmentId: string
  departmentName?: string
  mainContactName?: string
  mainContactEmail?: string
  billCodeName: string
  hireType: PositionHireType
  wcCode: string
  jobDescription?: string
  saveAsTemplate?: boolean
  markupPercentage?: number
  billablePercentage?: number
  nonBillablePercentage?: number
  status: 'Active' | 'Inactive' | 'Draft'
  type: 'Instance' | 'Template'
  createdDate?: Date
  modifiedDate?: Date
  createdBy?: string
  modifiedBy?: string
  versionKey?: number
}

export interface CreatePositionRequest {
  name: string
  companyId: string
  regionId: string // Region ID from the region dropdown
  departmentId: string
  siteId: string
  shift: number
  payType: number // PayType enum (0=None, 1=Hourly, 2=Salary)
  payRateMin: number
  payRateMax: number
  billCodeId: string
  hireType: string
  wcCodeId: string
  jobDescription?: string
  saveAsTemplate?: boolean
}

export interface UpdatePositionRequest {
  id: string
  positionName: string
  shift: PositionShift
  payType: number // PayType enum (0=None, 1=Hourly, 2=Salary)
  payRateMin: number
  payRateMax: number
  companyId: string
  regionId: string
  siteId: string
  departmentId: string
  billCodeName: string
  hireType: PositionHireType
  wcCode: string
  jobDescription?: string
  saveAsTemplate?: boolean
  markupPercentage?: number
  billablePercentage?: number
  nonBillablePercentage?: number
  versionKey?: number
}

export interface PositionResponse {
  id: string
  _id?: string // Alternative ID field from API
  name?: string // Position name from API
  positionName?: string // Alternative name field
  shift?: PositionShift
  payRateMin?: number
  payRateMax?: number
  companyId?: string
  companyName?: string // Populated company name
  regionId?: string // Optional - might need to be derived from site
  regionName?: string // Optional region name from API
  siteId?: string
  siteName?: string // Optional site name from API
  siteAddress?: {
    line1?: string
    city?: string
    state?: string
    zipCode?: string
    display?: string
    location?: {
      type?: string
      coordinates?: number[]
    }
  }
  departmentId?: string
  departmentName?: string
  mainContactName?: string
  mainContactEmail?: string
  billCodeId?: string // API returns billCodeId instead of billCodeName
  billCodeName?: string
  hireType?: PositionHireType
  wcCodeId?: string // API returns wcCodeId instead of wcCode
  wcCode?: string
  workerCompensationCode?: string // WC code display value
  payType?: string | number // Pay type (e.g., "2" for Hourly)
  jobDescription?: string
  markupPercentage?: number
  billablePercentage?: number
  nonBillablePercentage?: number
  status?: 'Active' | 'Inactive' | 'Draft'
  type?: 'Instance' | 'Template'
  // Contact info from department
  reportingManager?: string
  phoneNumber?: string
  email?: string
  // Audit fields
  createdDate?: Date | string
  modifiedDate?: Date | string
  createdBy?: string
  modifiedBy?: string
  versionKey?: number
}

export interface PositionPagingModel {
  positions?: PositionResponse[] // API returns "positions"
  data?: PositionResponse[] // Some endpoints might return "data"
  totalPositions?: number // API returns "totalPositions"
  totalCount?: number // Some endpoints might return "totalCount"
  currentPage?: number
  pageSize?: number
  totalPages?: number
}

export interface PositionPagingParametersRequest {
  pageNumber: number
  pageSize: number
  companyId?: string
  regionId?: string
  siteId?: string
  departmentId?: string
  status?: 'Active' | 'Inactive' | 'Draft'
  type?: 'Instance' | 'Template'
  searchTerm?: string
  sortBy?: string
  sortDirection?: 'asc' | 'desc'
}

export interface PositionSearchRequest {
  positionName?: string
  shift?: PositionShift
  companyId?: string
  regionId?: string
  siteId?: string
  departmentId?: string
  billCodeName?: string
  hireType?: PositionHireType
  wcCode?: string
  status?: 'Active' | 'Inactive' | 'Draft'
  type?: 'Instance' | 'Template'
  pageNumber?: number
  pageSize?: number
  sortBy?: string
  sortDirection?: 'asc' | 'desc'
}

// Paging Parameters Interface
export interface PagingParametersDto {
  pageNumber?: number
  pageSize?: number
  sortBy?: string
  sortDirection?: 'asc' | 'desc'
}

// Position Names by Company IDs Response
export interface PositionNamesByCompanyIdsResponse {
  positions: Record<string, string[]>
}

// Position In Use Response
export interface PositionInUseResponse {
  inUse: boolean
  serviceOrderCount: number
}

// Paycode Markup Response (from position/paycode combo)
export interface PaycodeMarkupResponse {
  markupPercentage: number | null
  billablePercentage: number | null
  nonBillablePercentage: number | null
}

/**
 * Position API Service
 * Handles all position related operations
 */
export class PositionApi extends BaseApi {
  /**
   * Get Position by ID
   */
  async getPositionById(id: string): Promise<PositionResponse> {
    try {
      const response = await this.client.get<PositionResponse>(`/api/position/${id}`)
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching position by ID:', error)
      throw error
    }
  }

  /**
   * Get Positions by Applicant ID
   * Returns positions associated with an applicant through their assignments
   * GET /api/position/applicant/:applicantId
   *
   * @param applicantId - The applicant ID
   * @param companyId - Optional company ID filter
   */
  async getPositionsByApplicantId(
    applicantId: string,
    companyId?: string,
  ): Promise<PositionResponse[]> {
    try {
      const params = companyId ? `?companyId=${companyId}` : ''
      const response = await this.client.get<PositionResponse[]>(
        `/api/position/applicant/${applicantId}${params}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching positions by applicant ID:', applicantId, error)
      throw error
    }
  }

  /**
   * Get Paycode Markup by Position and Paycode
   * Returns markup/billable percentages from bill code for a position/paycode combination
   * GET /api/position/:positionId/paycode/:paycodeId/markup
   *
   * @param positionId - The position ID
   * @param paycodeId - The paycode ID
   */
  async getPaycodeMarkupByPosition(
    positionId: string,
    paycodeId: string,
  ): Promise<PaycodeMarkupResponse> {
    try {
      const response = await this.client.get<PaycodeMarkupResponse>(
        `/api/position/${positionId}/paycode/${paycodeId}/markup`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching paycode markup:', {positionId, paycodeId}, error)
      throw error
    }
  }

  /**
   * Check if Position is in use (has active service orders)
   */
  async isPositionInUse(positionId: string): Promise<PositionInUseResponse> {
    try {
      const response = await this.client.get<PositionInUseResponse>(
        `/api/serviceorder/isPositionInUse/${positionId}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error checking if position is in use:', error)
      throw error
    }
  }

  /**
   * Get Positions with pagination (new endpoint)
   */
  async getPositions(params: {
    pageNumber: number
    pageSize: number
    companyId: string
    siteId?: string
    departmentId?: string
  }): Promise<PositionPagingModel> {
    try {
      const response = await this.client.post<PositionPagingModel>(`api/position/positions`, {
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
        companyId: params.companyId,
        siteId: params.siteId,
        departmentId: params.departmentId,
      })
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching positions:', error)
      throw error
    }
  }

  /**
   * Create a new Position
   */
  async createPosition(position: CreatePositionRequest): Promise<PositionResponse> {
    try {
      const sanitizedData = sanitizePayload(position as unknown as Record<string, unknown>)
      const response = await this.client.post<PositionResponse>('/api/position', sanitizedData)
      return response.data
    } catch (error: unknown) {
      console.error('Error creating position:', error)
      throw error
    }
  }

  /**
   * Update an existing Position
   */
  async updatePosition(position: UpdatePositionRequest): Promise<PositionResponse> {
    try {
      // Transform frontend field names to backend expected field names
      // Backend schema: id, name, companyId, regionId, departmentId, siteId, shift, payRateMin, payRateMax, billCodeId, hireType, wcCodeId, jobDescription, payType
      const transformedData = {
        id: position.id,
        name: position.positionName, // Backend expects 'name', not 'positionName'
        companyId: position.companyId,
        regionId: position.regionId, // Region ID from the region dropdown
        departmentId: position.departmentId,
        siteId: position.siteId,
        shift: Number(position.shift), // Ensure number
        payType: Number(position.payType), // Ensure number (0, 1, or 2)
        payRateMin: Number(position.payRateMin), // Ensure number
        payRateMax: Number(position.payRateMax), // Ensure number
        billCodeId: position.billCodeName, // Backend expects 'billCodeId', not 'billCodeName'
        hireType: position.hireType,
        wcCodeId: position.wcCode, // Backend expects 'wcCodeId', not 'wcCode'
        jobDescription: position.jobDescription,
      }
      const sanitizedData = sanitizePayload(transformedData as unknown as Record<string, unknown>)
      const response = await this.client.put<PositionResponse>('/api/position', sanitizedData)
      return response.data
    } catch (error: unknown) {
      console.error('Error updating position:', error)
      throw error
    }
  }

  /**
   * Delete a Position
   */
  async deletePosition(id: string): Promise<PositionResponse> {
    try {
      const response = await this.client.delete<PositionResponse>(`/api/position/${id}`)
      return response.data
    } catch (error: unknown) {
      console.error('Error deleting position:', error)
      throw error
    }
  }

  /**
   * Get Positions by IDs
   */
  async getPositionsByIds(ids: string[]): Promise<PositionResponse[]> {
    try {
      const response = await this.client.post<PositionResponse[]>(
        '/api/position/getPositionsByIds',
        {ids},
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching positions by IDs:', error)
      throw error
    }
  }

  /**
   * Archive a Position
   */
  async archivePosition(id: string): Promise<PositionResponse> {
    try {
      const response = await this.client.patch<PositionResponse>(`/position/archivePosition/${id}`)
      return response.data
    } catch (error: unknown) {
      console.error('Error archiving position:', error)
      throw error
    }
  }

  /**
   * Activate a Position
   */
  async activatePosition(id: string): Promise<PositionResponse> {
    try {
      const response = await this.client.patch<PositionResponse>(`/position/activatePosition/${id}`)
      return response.data
    } catch (error: unknown) {
      console.error('Error activating position:', error)
      throw error
    }
  }

  /**
   * Save Position as Template
   */
  async saveAsTemplate(positionId: string, templateName: string): Promise<PositionResponse> {
    try {
      const response = await this.client.post<PositionResponse>(
        `/position/saveAsTemplate/${positionId}`,
        {
          templateName,
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error saving position as template:', error)
      throw error
    }
  }

  /**
   * Get Position Templates
   */
  async getPositionTemplates(companyId?: string): Promise<PositionResponse[]> {
    try {
      const params = companyId ? `?companyId=${companyId}` : ''
      const response = await this.client.get<PositionResponse[]>(`/position/getTemplates${params}`)
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching position templates:', error)
      throw error
    }
  }

  /**
   * Export Positions data
   */
  async exportPositions(companyId: string, format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
    try {
      const response = await this.client.get(`/position/exportPositions/${companyId}`, {
        params: {format},
        responseType: 'blob',
      })
      return response.data
    } catch (error: unknown) {
      console.error('Error exporting positions:', error)
      throw error
    }
  }

  /**
   * Get Position Names by Company IDs
   * Returns position names grouped by company ID
   * POST /api/position/getPositionNamesByCompanyIds
   *
   * @param companyIds - Array of company IDs
   */
  async getPositionNamesByCompanyIds(
    companyIds: string[],
  ): Promise<PositionNamesByCompanyIdsResponse> {
    try {
      const response = await this.client.post<PositionNamesByCompanyIdsResponse>(
        '/api/position/getPositionNamesByCompanyIds',
        companyIds,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching position names by company IDs:', error)
      throw error
    }
  }
}

// Export singleton instance
export const positionApi = new PositionApi()
